<template>
  <div class="bg-container">
    <div class="main-container">
      <!-- 头部 -->
      <div class="header">
        <div class="title">厨房出菜</div>
        <div class="header-info">
          <select v-model="currentShop" class="restaurant-select" disabled>
            <option value="" disabled>请选择餐厅</option>
            <option v-for="shop in shops" :key="shop.shopId" :value="String(shop.shopId)">{{
              shop.shopName
            }}</option>
          </select>
          <span v-if="selectionMessage" class="text-sm text-gray-500">{{ selectionMessage }}</span>
          <select
            v-model="currentKitchen"
            :disabled="selectionLoading || !kitchens.length"
            class="restaurant-select"
          >
            <option value="" disabled>请选择厨房</option>
            <option value="all">全部厨房</option>
            <option v-for="kitchen in kitchens" :key="kitchen.id" :value="kitchen.id">
              {{ kitchen.name }}
            </option>
          </select>
          <div class="summary-badge">
            <span>{{ totalDishes }}</span
            >道菜品待出菜
          </div>
          <!-- 新增：刷新时间显示 -->
          <div class="refresh-time-info">
            <i class="fas fa-clock mr-2"></i>
            <span>{{ lastRefreshTime || '未刷新' }}</span>
          </div>
        </div>
        <div class="right-actions">
          <!-- 新增：统一导航菜单 -->
          <div class="nav-dropdown">
            <button class="nav-menu-btn" @click="toggleNavMenu">
              <i class="fas fa-bars"></i> 菜单
              <i class="fas fa-chevron-down"></i>
            </button>
            <div class="nav-menu" :class="{ show: showNavMenu }">
              <button class="nav-menu-item" @click="goToDishAssignment">
                <i class="fas fa-tasks"></i> 菜品分配
              </button>
              <button class="nav-menu-item" @click="goToKitchenOrder">
                <i class="fas fa-utensils"></i> 订单出餐
              </button>
              <button class="nav-menu-item" @click="goToServingHistory">
                <i class="fas fa-history"></i> 出餐历史
              </button>
              <button class="nav-menu-item" @click="goToOrderHistory">
                <i class="fas fa-receipt"></i> 订单查询
              </button>
              <button class="nav-menu-item" @click="goToTableManage">
                <i class="fas fa-table"></i> 桌位管理
              </button>
            </div>
          </div>
          <button class="refresh-btn" @click="refreshDishes">
            <i class="fas fa-sync-alt"></i> 刷新
          </button>
        </div>
      </div>

      <!-- 排序控制 -->
      <div class="sort-controls">
        <button
          v-for="item in sortOptions"
          :key="item.value"
          class="sort-btn"
          :class="{ active: currentSortBy === item.value }"
          @click="changeSort(item.value)"
        >
          <i :class="item.icon"></i> {{ item.label }}
        </button>
      </div>

      <!-- 菜品卡片区域 -->
      <div v-show="dishesData.length > 0" class="dish-cards">
        <div
          v-for="dish in sortedDishes"
          :key="dish.key"
          class="dish-card"
          :class="[dishCardClass(dish)]"
        >
          <div class="dish-header">
            <div
              v-if="currentSortBy === 'order'"
              class="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1"
            >
              <div class="dish-name break-words">{{ dish.table }}</div>
              <div class="break-all text-sm text-gray-600">
                订单号：<span>{{ String(dish.orderNo || dish.orderId || '').slice(0, -4) }}</span
                ><span class="text-3xl font-bold text-gray-900">{{
                  String(dish.orderNo || dish.orderId || '').slice(-4)
                }}</span>
              </div>
              <div class="time-info">
                <i class="fas fa-clock"></i> {{ getElapsedTime(dish.orderTime || '') }}
              </div>
            </div>
            <div v-else class="dish-title">
              <div class="dish-name">{{ dish.name }}</div>
            </div>
            <div class="dish-status">
              <span
                v-if="currentSortBy === 'order' && dish.orders[0].dineType"
                class="serving-dine-type"
              >
                {{ getDineTypeText(dish.orders[0].dineType) }}
              </span>
              <span v-if="dish.orders.some((o) => o.isUrgent)" class="urgent-badge">
                <i class="fas fa-fire" aria-hidden="true"></i> 加急
              </span>
            </div>
          </div>
          <div class="dish-content">
            <div
              v-for="order in dish.orders"
              :key="order.id"
              class="order-row"
              :class="{ urgent: order.isUrgent }"
            >
              <img
                v-if="order.imageUrl && !failedImageIds.has(order.id)"
                :src="order.imageUrl"
                :alt="order.name"
                class="serving-food-image"
                loading="lazy"
                @error="failedImageIds.add(order.id)"
              />
              <div class="order-info">
                <div class="serving-meta">
                  <template v-if="currentSortBy !== 'order'">
                    <span class="serving-table">{{ order.table }}</span>
                    <span v-if="order.dineType">{{ getDineTypeText(order.dineType) }}</span>
                    <span><i class="fas fa-clock"></i> {{ getElapsedTime(order.orderTime) }}</span>
                  </template>
                  <span>{{ order.kitchenName || '未知厨房' }}</span>
                  <span v-if="order.stove && order.stove !== '未知灶台'">{{ order.stove }}</span>
                  <span v-if="order.selfPickDate || order.selfPickTime" class="serving-pickup">
                    {{ order.selfPickDate }} {{ order.selfPickTime }}
                  </span>
                  <span
                    v-if="order.isUrgent || order.priority >= 666 || !isServingPaused(order)"
                    class="serving-operation"
                    :class="{
                      'is-urgent': order.isUrgent,
                      'is-called': !order.isUrgent && order.priority >= 666,
                    }"
                  >
                    <i v-if="order.isUrgent" class="fas fa-fire" aria-hidden="true"></i>
                    <i v-else-if="order.priority >= 666" class="fas fa-bell" aria-hidden="true"></i>
                    {{ order.isUrgent ? '加急' : order.priority >= 666 ? '叫菜' : '普通' }}
                  </span>
                  <span v-if="isServingPaused(order)" class="serving-operation is-paused">
                    <i class="fas fa-pause-circle" aria-hidden="true"></i>
                    {{ order.operateStatus === 1 ? '服务员暂停' : '厨师暂停' }}
                  </span>
                </div>
                <div class="serving-food-line">
                  <span v-if="currentSortBy === 'order'" class="serving-food-name">
                    {{ order.name }}
                  </span>
                  <span class="serving-quantity">× {{ order.quantity }}</span>
                  <span
                    v-if="order.foodSpecName && order.foodSpecName !== '标准'"
                    class="serving-food-detail"
                  >
                    {{ order.foodSpecName }}
                  </span>
                  <span v-if="order.foodCookDesc?.trim()" class="serving-food-detail">
                    {{ order.foodCookDesc }}
                  </span>
                </div>
              </div>
              <div class="order-actions">
                <button
                  class="action-btn serve-btn"
                  :disabled="order.operateStatus === 1 || servingIds.has(order.id)"
                  :title="order.operateStatus === 1 ? '服务员已暂停，恢复后可出菜' : '出菜'"
                  @click="serveOrder(order)"
                >
                  <i :class="order.operateStatus === 1 ? 'fas fa-pause' : 'fas fa-utensils'"></i>
                  {{
                    order.operateStatus === 1
                      ? '已暂停'
                      : servingIds.has(order.id)
                      ? '出餐中...'
                      : '出菜'
                  }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-show="dishesData.length === 0" class="empty-state">
        <i class="fas fa-utensils"></i>
        <h3>暂无待出菜品</h3>
        <p>所有菜品都已完成出菜</p>
      </div>

      <!-- 小票模态框 -->
      <!-- <div v-show="showReceiptModal" class="modal" @click.self="closeReceiptModal">
        <div class="modal-content">
          <div class="modal-header">
            <h3>出菜小票【自动打印】</h3>
            <span class="close" @click="closeReceiptModal">&times;</span>
          </div>
          <div class="receipt">
            <div class="receipt-header">
              <div
                class="receipt-title"
                style="font-size: 22px; font-weight: bold; margin-bottom: 10px"
                >出菜小票</div
              >
            </div>
            <div class="receipt-item" style="margin-bottom: 18px">
              <span style="font-size: 22px; font-weight: bold">{{ receiptOrder?.dishName }}</span>
            </div>
            <div class="receipt-item" style="margin-bottom: 18px">
              <span style="font-size: 22px; font-weight: bold">{{ receiptOrder?.table }}</span>
            </div>
            <div class="receipt-item">
              <span>灶台:</span>
              <span style="font-size: 16px; font-weight: 600">{{ receiptOrder?.stove }}</span>
            </div>
            <div class="receipt-item">
              <span>备注:</span>
              <span style="font-size: 16px; font-weight: 600"></span>
            </div>
            <div class="receipt-item">
              <span>出菜时间:</span>
              <span style="font-size: 16px; font-weight: 600">{{ receiptOrderTime }}</span>
            </div>
          </div>
          <button class="print-btn" @click="printReceipt">
            <i class="fas fa-print"></i> 打印小票
          </button>
        </div>
      </div> -->
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, onMounted, watch, onUnmounted } from 'vue';
  import { useRestaurantKitchen } from '@/composables/useRestaurantKitchen';
  import { fetchRestaurantKitchenFoods } from '@/service/restaurantKitchen';
  import { outFood } from '@/service/food';
  import type { KitchenServingItemModel, KitchenServingGroupModel } from '@/model/kitchenServing';

  // 新增：新订单提示音
  let newOrderAudio: HTMLAudioElement | null = null;
  const initNewOrderAudio = () => {
    try {
      const src = new URL('@/assets/audio/new_order.mp3', import.meta.url).href;
      newOrderAudio = new Audio(src);
      newOrderAudio.preload = 'auto';
      newOrderAudio.volume = 1.0;
    } catch (e) {
      console.warn('新订单提示音初始化失败:', e);
    }
  };
  const playNewOrderSound = async () => {
    try {
      if (newOrderAudio) {
        newOrderAudio.currentTime = 0;
        await newOrderAudio.play();
      }
    } catch (e) {
      console.warn('播放新订单提示音失败:', e);
    }
  };

  // 新增：本地存储最近订单创建时间（按厨房）
  const LAST_ORDER_CREATE_TIME_PREFIX = 'kitchen_serving_last_create_time_';
  const getLastCreateTimeKey = (kitchenId: string) =>
    `${LAST_ORDER_CREATE_TIME_PREFIX}${currentShop.value}_${kitchenId}`;
  const getStoredLastCreateTime = (kitchenId: string): number | null => {
    try {
      const v = localStorage.getItem(getLastCreateTimeKey(kitchenId));
      return v ? parseInt(v, 10) : null;
    } catch {
      return null;
    }
  };
  const setStoredLastCreateTime = (kitchenId: string, ms: number) => {
    try {
      localStorage.setItem(getLastCreateTimeKey(kitchenId), String(ms));
    } catch (e) {
      console.warn('无法保存最近创建时间到localStorage:', e);
    }
  };

  const {
    shops,
    currentShop,
    kitchens,
    currentKitchen,
    selectionVersion,
    selectionLoading,
    selectionMessage,
    loadShops,
  } = useRestaurantKitchen('kitchen_serving_selected_kitchen');
  const lastRefreshTime = ref('');
  let autoRefreshTimer: NodeJS.Timeout | null = null;
  const showNavMenu = ref(false);
  const sortOptions = [
    { value: 'order', label: '按订单排序', icon: 'fas fa-receipt' },
    { value: 'time', label: '按时间排序', icon: 'fas fa-clock' },
    { value: 'priority', label: '按优先级排序', icon: 'fas fa-exclamation-triangle' },
  ];
  const currentSortBy = ref('order');
  const servingIds = ref(new Set<string>());
  const failedImageIds = ref(new Set<string>());

  // 两种展示模式共用逐份菜品数据，出菜后同步更新。
  const servingItems = ref<KitchenServingItemModel[]>([]);

  function splitMultipleQuantityOrders(
    orders: KitchenServingItemModel[],
  ): KitchenServingItemModel[] {
    return orders.flatMap((order) =>
      Array.from({ length: Math.max(1, order.quantity) }, (_, index) => ({
        ...order,
        id: `${order.orderOutId}:${index}`,
        quantity: 1,
      })),
    );
  }

  function getOrderTimeMs(orderTime: string): number {
    const time = new Date(orderTime).getTime();
    return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
  }

  function getEarliestOrder(orders: KitchenServingItemModel[]): KitchenServingItemModel {
    return orders.reduce((earliest, order) =>
      getOrderTimeMs(order.orderTime) < getOrderTimeMs(earliest.orderTime) ? order : earliest,
    );
  }

  const dishesData = computed<KitchenServingGroupModel[]>(() => {
    const grouped = new Map<string, KitchenServingGroupModel>();
    servingItems.value.forEach((order) => {
      const key = `dish:${order.name}`;
      let group = grouped.get(key);
      if (!group) {
        group = { key, name: order.name, type: order.type, typeName: order.typeName, orders: [] };
        grouped.set(key, group);
      }
      group.orders.push(order);
    });
    return [...grouped.values()];
  });

  const orderGroups = computed<KitchenServingGroupModel[]>(() => {
    const grouped = new Map<number, KitchenServingGroupModel>();
    servingItems.value.forEach((order) => {
      let group = grouped.get(order.orderId);
      if (!group) {
        group = {
          key: `order:${order.orderId}`,
          name: order.name,
          type: order.type,
          typeName: order.typeName,
          orderId: order.orderId,
          orderNo: order.orderNo,
          table: order.table,
          orders: [],
        };
        grouped.set(order.orderId, group);
      }
      group.orders.push(order);
    });
    return [...grouped.values()]
      .map((group) => ({ ...group, orderTime: getEarliestOrder(group.orders).orderTime }))
      .sort(
        (a, b) =>
          getOrderTimeMs(a.orderTime) - getOrderTimeMs(b.orderTime) ||
          (a.orderId ?? 0) - (b.orderId ?? 0),
      );
  });

  const sortedDishes = computed(() => {
    const sorted = [...dishesData.value];
    switch (currentSortBy.value) {
      case 'order':
        return orderGroups.value;
      case 'time':
        return sorted.sort((a, b) => {
          const aEarliest = Math.min(...a.orders.map((o) => new Date(o.orderTime).getTime()));
          const bEarliest = Math.min(...b.orders.map((o) => new Date(o.orderTime).getTime()));
          return aEarliest - bEarliest;
        });
      case 'priority':
        return sorted.sort((a, b) => {
          const aUrgent = a.orders.some((o) => o.isUrgent);
          const bUrgent = b.orders.some((o) => o.isUrgent);
          if (aUrgent !== bUrgent) return Number(bUrgent) - Number(aUrgent);
          const aMaxPriority = Math.max(...a.orders.map((o) => o.priority));
          const bMaxPriority = Math.max(...b.orders.map((o) => o.priority));
          return bMaxPriority - aMaxPriority;
        });
      default:
        return sorted;
    }
  });

  const totalDishes = computed(() => servingItems.value.length);

  function getElapsedTime(orderTime: string) {
    const now = new Date();
    const order = new Date(orderTime);
    if (Number.isNaN(order.getTime())) return '时间未知';
    const diffMs = Math.max(0, now.getTime() - order.getTime());
    const diffMins = Math.floor(diffMs / 60000);
    return `${diffMins}分钟`;
  }

  function isServingPaused(order: KitchenServingItemModel): boolean {
    return order.operateStatus === 1 || order.kitchenStatus === 9;
  }

  function dishCardClass(dish: KitchenServingGroupModel) {
    const hasUrgent = dish.orders.some((order) => order.isUrgent);
    const hasReady = dish.orders.some((order) => order.status === 'ready');
    const hasCooking = dish.orders.some((order) => order.status === 'cooking');
    let overallStatus = 'waiting';
    if (hasReady) overallStatus = 'ready';
    else if (hasCooking) overallStatus = 'cooking';
    return [hasUrgent ? 'urgent' : '', overallStatus];
  }

  function changeSort(sortBy: string) {
    currentSortBy.value = sortBy;
  }

  // 新增：导航菜单切换方法
  const toggleNavMenu = () => {
    showNavMenu.value = !showNavMenu.value;
  };

  // 新增：导航跳转方法
  const goToDishAssignment = () => {
    window.location.href = '/dishAssignment';
    showNavMenu.value = false; // 关闭菜单
  };

  const goToKitchenOrder = () => {
    window.location.href = '/kitchenOrder';
    showNavMenu.value = false; // 关闭菜单
  };

  const goToServingHistory = () => {
    window.location.href = '/servingHistory';
    showNavMenu.value = false; // 关闭菜单
  };

  const goToOrderHistory = () => {
    window.location.href = '/orderHistory';
    showNavMenu.value = false; // 关闭菜单
  };

  const goToTableManage = () => {
    window.location.href = '/tableManage';
    showNavMenu.value = false; // 关闭菜单
  };

  // 厨房相关方法
  // 加载菜品列表
  let foodRequest = 0;
  const loadFoodList = async () => {
    if (!currentKitchen.value) {
      return;
    }

    const version = selectionVersion.value;
    const request = ++foodRequest;
    try {
      const kitchenIds =
        currentKitchen.value === 'all'
          ? kitchens.value.map((item) => Number(item.id))
          : [Number(currentKitchen.value)];
      const details = await fetchRestaurantKitchenFoods(Number(currentShop.value), kitchenIds);
      if (version !== selectionVersion.value || request !== foodRequest) return;
      const apiOrders: KitchenServingItemModel[] = [];
      details.forEach((detail) => {
        const priority = Number(detail.priority) || 1;
        const operateStatus = Number(detail.operateStatus) || 0;
        const order: KitchenServingItemModel = {
          id: `${detail.orderOutId}`,
          orderOutId: detail.orderOutId,
          orderNo: detail.orderNo,
          kitchenId: detail.kitchenId,
          kitchenName: detail.kitchenName,
          kitchenStatus: Number(detail.kitchenStatus),
          imageUrl: detail.imageUrl,
          selfPickDate: detail.selfPickDate,
          selfPickTime: detail.selfPickTime,
          name: detail.shopFoodName || detail.foodName || '',
          type: getFoodTypeString(detail.foodType),
          typeName: getFoodTypeName(detail.foodType),
          table: detail.tableName || '未知桌号',
          quantity: detail.quantity || 1,
          orderTime: detail.createTime || '',
          status: detail.kitchenStatus === 8 ? 'ready' : 'cooking',
          stove: detail.stoveName || '未知灶台',
          isUrgent: operateStatus === 10 || priority >= 999,
          priority,
          operateStatus,
          foodSpecName: detail.foodSpecName || '', // 添加规格名称
          foodCookDesc: detail.foodCookDesc || '', // 添加口味描述
          dineType: detail.dineType || 0, // 添加用餐类型
          // 保存API所需字段
          orderId: detail.orderId,
          orderFoodId: detail.orderFoodId,
          shopId: detail.shopId,
          shopFoodId: detail.shopFoodId,
        };
        apiOrders.push(order);
      });

      // 更新数据
      const splitOrders = splitMultipleQuantityOrders(apiOrders);
      failedImageIds.value.clear();
      servingItems.value = splitOrders;

      // 新增：检查是否有新订单，播放提示音
      if (apiOrders.length > 0) {
        // 找到最晚的创建时间
        let latestCreateTime = 0;
        apiOrders.forEach((order) => {
          const createTime = new Date(order.orderTime || '').getTime();
          if (!Number.isNaN(createTime) && createTime > latestCreateTime) {
            latestCreateTime = createTime;
          }
        });

        if (latestCreateTime > 0) {
          const kitchenId = currentKitchen.value;
          const prev = getStoredLastCreateTime(kitchenId);
          if (prev !== null && latestCreateTime > prev) {
            showToast('您有新的订单，请注意查收');
            playNewOrderSound();
          }
          setStoredLastCreateTime(kitchenId, latestCreateTime);
        }
      }
    } catch (error) {
      if (version !== selectionVersion.value || request !== foodRequest) return;
      showToast('加载菜品失败，请重试');
      console.error('菜品列表加载异常:', error);
      // 异常时清空列表
      servingItems.value = [];
    }
  };

  // 菜品类型映射
  const getFoodTypeString = (foodType: number) => {
    switch (foodType) {
      case 1:
        return 'hot';
      case 2:
        return 'cold';
      case 3:
        return 'soup';
      case 4:
        return 'dim-sum';
      case 5:
        return 'drink';
      default:
        return 'hot';
    }
  };

  const getFoodTypeName = (foodType: number) => {
    switch (foodType) {
      case 1:
        return '热菜';
      case 2:
        return '冷菜';
      case 3:
        return '汤类';
      case 4:
        return '点心';
      case 5:
        return '饮品';
      default:
        return '热菜';
    }
  };

  // 用餐类型映射
  const getDineTypeText = (dineType: number) => {
    switch (dineType) {
      case 1:
        return '堂食';
      case 2:
        return '自提';
      case 3:
        return '外卖';
      case 4:
        return '打包';
      default:
        return '未知';
    }
  };

  // 小票相关
  const showReceiptModal = ref(false);
  const receiptOrder = ref<KitchenServingItemModel | null>(null);
  const receiptOrderTime = ref('');

  async function serveOrder(order: KitchenServingItemModel) {
    if (servingIds.value.has(order.id)) return;
    const latestOrder = servingItems.value.find((item) => item.id === order.id);
    if (!latestOrder) return;
    if (latestOrder.operateStatus === 1) {
      showToast('服务员已暂停该菜品，恢复后才可出菜');
      return;
    }
    const version = selectionVersion.value;

    // 数据验证
    if (!order.orderId || !order.orderFoodId || !order.shopId) {
      console.error('出餐失败：缺少必要的API参数', {
        orderId: order.orderId,
        orderFoodId: order.orderFoodId,
        shopId: order.shopId,
      });
      alert('出餐失败：数据异常，请刷新后重试');
      return;
    }

    servingIds.value.add(order.id);

    try {
      // 调用出餐API
      const response = await outFood({
        orderId: order.orderId,
        orderFoodId: order.orderFoodId,
        shopId: order.shopId,
      });

      if (version !== selectionVersion.value) return;
      if (response.code === 200) {
        console.log('出餐成功:', response);

        // 记录小票信息
        // receiptOrder.value = { ...order, dishName: dish.name, dishTypeName: dish.typeName };
        // receiptOrderTime.value = new Date().toLocaleString();
        // showReceiptModal.value = true;

        servingItems.value = servingItems.value.filter((item) => item.id !== order.id);

        // 可选：刷新数据
        setTimeout(() => {
          loadFoodList();
        }, 1000);
      } else {
        console.error('出餐API调用失败:', response);
        alert(`出餐失败：${response.msg || '未知错误'}`);
      }
    } catch (error) {
      console.error('出餐异常:', error);
      alert('出餐失败：网络异常，请稍后重试');
    } finally {
      servingIds.value.delete(order.id);
    }
  }

  function closeReceiptModal() {
    showReceiptModal.value = false;
  }

  function printReceipt() {
    if (!receiptOrder.value) return;
    const printContent = `
    <div class='receipt-header'>
      <div class='receipt-title' style='font-size: 22px; font-weight: bold; margin-bottom: 10px;'>出菜小票</div>
    </div>
    <div class='receipt-item' style='margin-bottom: 18px;'>
      <span style='font-size: 22px; font-weight: bold;'>${receiptOrder.value.dishName}</span>
    </div>
    <div class='receipt-item' style='margin-bottom: 18px;'>
      <span style='font-size: 22px; font-weight: bold;'>${receiptOrder.value.table}</span>
    </div>
    <div class='receipt-item'>
      <span>灶台:</span>
      <span style='font-size: 16px; font-weight: 600;'>${receiptOrder.value.stove}</span>
    </div>
    <div class='receipt-item'>
      <span>备注:</span>
      <span style='font-size: 16px; font-weight: 600;'></span>
    </div>
    <div class='receipt-item'>
      <span>出菜时间:</span>
      <span style='font-size: 16px; font-weight: 600;'>${receiptOrderTime.value}</span>
    </div>
  `;
    const printWindow = window.open('', '_blank');
    printWindow?.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>出菜小票</title>
  <style>
    body { font-family: 'Courier New', monospace; font-size: 16px; margin: 20px; }
    .receipt-header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 10px; margin-bottom: 15px; }
    .receipt-title { font-size: 22px; font-weight: bold; margin-bottom: 10px; }
    .receipt-item { display: flex; justify-content: space-between; margin-bottom: 18px; }
    .receipt-item span:first-child { font-size: 22px; font-weight: bold; }
    .receipt-item span:last-child { font-size: 22px; font-weight: bold; }
  </style>
</head>
<body>
  ${printContent}
</body>
</html>
`);
    printWindow?.document.close();
    printWindow?.print();
    printWindow?.close();
  }

  // 刷新菜品列表
  const refreshDishes = () => {
    loadFoodList();
    updateRefreshTime(); // 新增：更新时间
    showToast('菜品列表已刷新');
  };

  // 新增：更新刷新时间
  const updateRefreshTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    lastRefreshTime.value = `${hours}:${minutes}:${seconds}`;
  };

  // 新增：启动自动刷新定时器
  const startAutoRefresh = () => {
    // 清除现有定时器
    if (autoRefreshTimer) {
      clearInterval(autoRefreshTimer);
    }

    // 设置10秒定时器
    autoRefreshTimer = setInterval(async () => {
      if (currentKitchen.value) {
        await loadFoodList();
        updateRefreshTime();
        console.log('自动刷新完成:', lastRefreshTime.value);
      }
    }, 10000); // 10秒
  };

  // 新增：停止自动刷新定时器
  const stopAutoRefresh = () => {
    if (autoRefreshTimer) {
      clearInterval(autoRefreshTimer);
      autoRefreshTimer = null;
    }
  };

  // 显示提示消息
  const showToast = (message: string) => {
    // 创建提示框
    const toast = document.createElement('div');
    toast.className =
      'fixed top-4 right-4 bg-gray-900 text-white px-4 py-2 rounded-lg z-50 transform translate-x-full transition-transform duration-300';
    toast.textContent = message;
    document.body.appendChild(toast);

    // 显示动画
    setTimeout(() => {
      toast.classList.remove('translate-x-full');
    }, 100);

    // 自动隐藏
    setTimeout(() => {
      toast.classList.add('translate-x-full');
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 2000);
  };

  // 新增：点击外部关闭菜单
  const handleClickOutside = (event: Event) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.nav-dropdown')) {
      showNavMenu.value = false;
    }
  };

  // 生命周期钩子
  onMounted(() => {
    loadShops();
    // 新增：初始化刷新时间并启动自动刷新
    updateRefreshTime();
    startAutoRefresh();
    initNewOrderAudio(); // 初始化新订单提示音
    // 添加点击外部关闭菜单的事件监听
    document.addEventListener('click', handleClickOutside);
  });

  watch(
    selectionVersion,
    () => {
      servingItems.value = [];
      showReceiptModal.value = false;
    },
    { flush: 'sync' },
  );

  // 监听厨房变化
  watch(currentKitchen, (newVal) => {
    if (newVal) {
      // 保存当前选择的厨房到localStorage
      loadFoodList();
      // 新增：厨房变化时更新时间
      updateRefreshTime();
    }
  });

  // 新增：组件卸载时清理定时器
  onUnmounted(() => {
    stopAutoRefresh();
    newOrderAudio = null;
    // 移除事件监听器
    document.removeEventListener('click', handleClickOutside);
  });
</script>
<style scoped>
  .header-info,
  .restaurant-selector,
  .title-section {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  /* 直接粘贴原有 style 内容，无需变动 */
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
    background-image: url('@/assets/bg.jpg');
    background-size: 100% 100%;
    background-attachment: fixed;
    color: #1d1d1f;
    line-height: 1.6;
    margin: 0;
    padding: 0;
    overflow-x: hidden;
  }

  .bg-container {
    margin: 0 auto;
    background-image: url('@/assets/bg.jpg');
    background-size: 100% 100%;
    background-attachment: fixed;
    color: #1d1d1f;
    padding: 10px;
    line-height: 1.6;
    min-height: 100vh;
    overflow-x: hidden;
  }
  .main-container {
    max-width: 1400px;
    margin: 0 auto;
    min-height: calc(100vh - 20px);
    overflow-y: auto;
    overflow-x: hidden;
    padding-bottom: 20px;
  }
  /* 头部 */
  .header {
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
    margin-bottom: 20px;
  }
  .title {
    font-size: 40px;
    font-weight: 700;
    color: #ffffff;
  }
  .header-info {
    display: flex;
    align-items: center;
    gap: 15px;
    flex-wrap: wrap;
  }
  .restaurant-select {
    background: rgba(255, 255, 255, 0.1);
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    color: #ffffff;
    font-size: 16px;
    font-weight: 600;
    padding: 8px 40px 8px 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    min-width: 180px;
    appearance: none;
  }
  .restaurant-select option {
    background-color: #1c1c1e;
    color: #ffffff;
    padding: 12px;
    font-size: 18px;
    font-weight: 500;
  }
  .restaurant-select:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }
  .summary-badge {
    color: rgb(255, 0, 0);
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 22px;
    font-weight: 600;
  }
  .refresh-time-info {
    display: flex;
    align-items: center;
    color: #ff3b30;
    font-size: 18px;
    margin-left: 15px;
  }

  .refresh-time-info i {
    margin-right: 8px;
    color: #ff3b30;
  }
  .nav-buttons {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .right-actions {
    display: flex;
    align-items: center;
    gap: 15px;
    flex-wrap: wrap;
  }

  .nav-btn {
    background: linear-gradient(135deg, #007aff, #0051d5);
    color: white;
    border: none;
    padding: 12px 14px;
    border-radius: 16px;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
  }
  .nav-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 122, 255, 0.4);
  }
  .nav-btn:active {
    transform: translateY(0);
  }

  /* 新增：导航菜单样式 */
  .nav-dropdown {
    position: relative;
    display: inline-block;
  }

  .nav-menu-btn {
    background: #007aff;
    color: white;
    border: none;
    padding: 12px 16px;
    border-radius: 16px;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
  }

  .nav-menu-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 122, 255, 0.4);
  }

  .nav-menu-btn:active {
    transform: translateY(0);
  }

  .nav-menu {
    position: absolute;
    top: 100%;
    right: 0;
    background-color: #ffffff;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border: 1px solid #e5e5ea;
    min-width: 200px;
    z-index: 1000;
    padding: 8px 0;
    margin-top: 8px;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-10px);
    transition: all 0.2s ease;
  }

  .nav-menu.show {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
  }

  .nav-menu-item {
    display: block;
    width: 100%;
    padding: 12px 16px;
    background: none;
    border: none;
    text-align: left;
    font-size: 16px;
    color: #000000;
    cursor: pointer;
    border-radius: 8px;
    margin: 2px 8px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .nav-menu-item:hover {
    background-color: rgba(0, 122, 255, 0.1);
    color: #007aff;
  }

  .nav-menu-item:active {
    transform: scale(0.98);
  }

  .nav-menu-item i {
    width: 16px;
    font-size: 14px;
  }
  .refresh-btn {
    background: linear-gradient(135deg, #007aff, #0051d5);
    color: white;
    border: none;
    padding: 12px 14px;
    border-radius: 16px;
    font-size: 18px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
  }
  .refresh-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 122, 255, 0.4);
  }
  .refresh-btn:active {
    transform: translateY(0);
  }
  /* 排序控制 */
  .sort-controls {
    background: rgba(255, 255, 255, 0.9);
    padding: 15px;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin-bottom: 20px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    backdrop-filter: blur(10px);
  }
  .sort-btn {
    background: #f1f1f1;
    border: none;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
  }
  .sort-btn.active {
    background: #007aff;
    color: white;
  }
  .sort-btn:hover {
    background: #e1e1e1;
  }
  .sort-btn.active:hover {
    background: #0056b3;
  }
  /* 菜品网格 */
  .dish-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 12px;
    align-items: start;
  }
  /* 菜品卡片 */
  .dish-card {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 12px;
    padding: 5px;
    text-align: left;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    border: 2px solid transparent;
    transition: all 0.2s;
    backdrop-filter: blur(10px);
  }
  .dish-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }
  .dish-card.ready {
    border-color: #34c759;
  }
  .dish-card.urgent {
    border-color: #ff3b30;
  }
  /* 卡片头部 */
  .dish-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    padding: 10px 8px;
    gap: 8px;
    flex-wrap: wrap;
    border-bottom: 1px solid #e1e1e1;
  }
  .dish-title {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .dish-name {
    font-size: 20px;
    font-weight: 600;
    color: #1d1d1f;
  }
  .dish-type {
    background: #007aff;
    color: white;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
  }
  .dish-type.hot {
    background: #ff9500;
  }
  .dish-type.cold {
    background: #5ac8fa;
  }
  .dish-type.dim-sum {
    background: #34c759;
  }
  .dish-type.drink {
    background: #007aff;
  }
  .dish-type.soup {
    background: #ff6b6b;
  }
  .dish-status {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .status-badge {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
  }
  .status-badge.waiting {
    background: #ffc107;
    color: white;
  }
  .status-badge.cooking {
    background: #28a745;
    color: white;
  }
  .status-badge.ready {
    background: #007aff;
    color: white;
  }
  .urgent-badge {
    background: #ff3b30;
    color: white;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 600;
  }
  .urgent-badge-inline {
    background: #ff3b30;
    color: white;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    animation: urgentPulse 2s infinite;
  }
  .urgent-table {
    background: #ff3b30 !important;
    color: white !important;
    border: 2px solid #ff1a1a !important;
    box-shadow: 0 0 8px rgba(255, 59, 48, 0.4);
  }
  .paused-table {
    background: #ff3b30 !important;
    color: white !important;
    /* border: 2px solid #ff1a1a !important; */
    box-shadow: 0 0 8px rgba(255, 59, 48, 0.4);
  }
  @keyframes urgentPulse {
    0% {
      box-shadow: 0 0 0 0 rgba(255, 59, 48, 0.7);
    }
    70% {
      box-shadow: 0 0 0 8px rgba(255, 59, 48, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(255, 59, 48, 0);
    }
  }
  /* 订单内的菜品采用图片、信息、操作三列布局。 */
  .order-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 12px;
    margin: 8px 0;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: transparent;
  }
  .order-row.urgent {
    border-color: #ff8d7e;
    background: #fff0ec;
  }
  .serving-food-image {
    width: 72px;
    height: 72px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
  }
  .order-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    flex: 1;
    min-width: 0;
  }
  .serving-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    color: #64748b;
    font-size: 12px;
    line-height: 1.4;
  }
  .serving-table {
    color: #2563eb;
    font-weight: 700;
  }
  .serving-pickup {
    color: #7c3aed;
    font-weight: 600;
  }
  .serving-operation {
    padding: 2px 6px;
    border-radius: 4px;
    background: #eef2f6;
    color: #64748b;
    font-weight: 600;
  }
  .serving-operation.is-urgent {
    color: #ffffff;
    background: #c62828;
  }
  .serving-operation.is-called {
    color: #ffffff;
    background: #a94b08;
  }
  .serving-operation.is-paused {
    color: #b45309;
    background: #fef3c7;
  }
  .serving-dine-type {
    padding: 6px 12px;
    border-radius: 6px;
    background: #7c3aed;
    color: #ffffff;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.4;
    white-space: nowrap;
  }
  .serving-food-line {
    color: #1e293b;
    font-size: 20px;
    line-height: 1.5;
    overflow-wrap: anywhere;
  }
  .serving-food-name {
    font-weight: 700;
  }
  .serving-quantity {
    display: inline-block;
    margin-left: 8px;
    font-size: 24px;
    font-weight: 700;
    white-space: nowrap;
    vertical-align: baseline;
  }
  .serving-food-detail {
    display: inline-block;
    max-width: 100%;
    margin-left: 10px;
    color: #64748b;
    font-size: 14px;
    font-weight: 400;
  }
  .time-info {
    font-size: 12px;
    color: #6c757d;
    white-space: nowrap;
    flex-shrink: 0;
  }
  /* 操作按钮 */
  .order-actions {
    display: flex;
    align-self: center;
    gap: 5px;
    flex-shrink: 0;
    min-width: 80px;
  }
  .action-btn {
    min-width: 104px;
    min-height: 52px;
    padding: 12px 18px;
    border: none;
    border-radius: 8px;
    font-size: 18px;
    font-weight: 600;
    line-height: 1.4;
    cursor: pointer;
    transition: all 0.2s;
  }
  .action-btn.serve-btn {
    background: #007aff;
    color: white;
  }
  .action-btn:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
  .action-btn.serve-btn:disabled {
    background: #94a3b8;
    cursor: not-allowed;
    opacity: 1;
    transform: none;
  }
  /* 空状态 */
  .empty-state {
    text-align: center;
    padding: 60px 20px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(10px);
  }
  .empty-state i {
    font-size: 48px;
    color: #ccc;
    margin-bottom: 16px;
  }
  /* 小票模态框 */
  .modal {
    position: fixed;
    z-index: 1000;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
  .modal-content {
    background-color: white;
    margin: 5% auto;
    padding: 0;
    border-radius: 12px;
    width: 400px;
    max-width: 90%;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
  .modal-header {
    background: #007aff;
    color: white;
    padding: 15px 20px;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .close {
    color: white;
    font-size: 24px;
    font-weight: bold;
    cursor: pointer;
  }
  .close:hover {
    opacity: 0.7;
  }
  .receipt {
    padding: 20px;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    line-height: 1.4;
  }
  .receipt-header {
    text-align: center;
    border-bottom: 2px dashed #ccc;
    padding-bottom: 10px;
    margin-bottom: 15px;
  }
  .receipt-title {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 5px;
  }
  .receipt-item {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  .receipt-footer {
    border-top: 2px dashed #ccc;
    padding-top: 10px;
    margin-top: 15px;
    text-align: center;
    font-size: 12px;
    color: #666;
  }
  .print-btn {
    background: #28a745;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    margin: 15px 20px 20px;
    width: calc(100% - 40px);
  }
  .print-btn:hover {
    background: #218838;
  }
  /* 响应式 */
  @media (max-width: 768px) {
    .header {
      flex-direction: column;
      align-items: stretch;
    }
    .header-info {
      justify-content: space-between;
    }
    .dish-cards {
      grid-template-columns: 1fr;
    }
    .order-row {
      gap: 10px;
      padding: 12px 10px;
    }
    .serving-food-image {
      width: 56px;
      height: 56px;
    }
    .serving-food-line {
      font-size: 18px;
    }
    .serving-quantity {
      margin-left: 6px;
      font-size: 22px;
    }
    .serving-food-detail {
      margin-left: 8px;
      font-size: 13px;
    }
    .order-actions {
      min-width: 0;
    }
  }
  @media (max-width: 400px) {
    .order-row {
      flex-wrap: wrap;
    }
    .order-info {
      flex-basis: calc(100% - 66px);
    }
    .order-actions {
      margin-left: auto;
      width: 100%;
    }
    .action-btn.serve-btn {
      width: 100%;
    }
  }
</style>
